import React, { useState, useEffect } from 'react';
import { useCart } from 'react-use-cart';
import { useNavigate } from 'react-router-dom';
import MarketingForecast from '../../../components/MarketingForecast';
import '../../../Assets/Stylesheets/Pages/MarketingServices/OptionAIPaid.scss';
import API_BASE_URL from '../../../config/api';
import { authHeaders } from '../../../utils/apiAuth';

// Import all the platform logos
import googleLogo from '../../../Assets/Images/Google_logo.png';
import youtubeLogo from '../../../Assets/Images/Youtube_logo.png';
import facebookLogo from '../../../Assets/Images/Facebook_logo.png';
import instagramLogo from '../../../Assets/Images/Instagram_logo.png';
import tiktokLogo from '../../../Assets/Images/Tiktok_logo.png';
import xLogo from '../../../Assets/Images/X_logo.png';
import linkedinLogo from '../../../Assets/Images/Linkedin_logo.png';
import pinterestLogo from '../../../Assets/Images/Pinterest_logo.png';
import spotifyLogo from '../../../Assets/Images/Spotify_logo.png';
import snapchatLogo from '../../../Assets/Images/Snapchat_logo.png';
import amazonLogo from '../../../Assets/Images/Amazon_logo.png';

const platformLogos = {
  Google: googleLogo,
  YouTube: youtubeLogo,
  Facebook: facebookLogo,
  Instagram: instagramLogo,
  TikTok: tiktokLogo,
  X: xLogo,
  LinkedIn: linkedinLogo,
  Pinterest: pinterestLogo,
  Spotify: spotifyLogo,
  SnapChat: snapchatLogo,
  Amazon: amazonLogo,
};

const MIN_BUDGET = 500;

// Array to hold the platforms' data
const platforms = [
  { id: 'google', name: 'Google', logo: googleLogo },
  { id: 'youtube', name: 'YouTube', logo: youtubeLogo },
  { id: 'facebook', name: 'Facebook', logo: facebookLogo },
  { id: 'instagram', name: 'Instagram', logo: instagramLogo },
  { id: 'tiktok', name: 'TikTok', logo: tiktokLogo },
  { id: 'x', name: 'X', logo: xLogo },
  { id: 'linkedin', name: 'LinkedIn', logo: linkedinLogo },
  { id: 'pinterest', name: 'Pinterest', logo: pinterestLogo },
  { id: 'spotify', name: 'Spotify', logo: spotifyLogo },
  { id: 'snapchat', name: 'Snapchat', logo: snapchatLogo },
  { id: 'amazon', name: 'Amazon', logo: amazonLogo },
];

export default function OptionAIPaid() {
  const [budgetType, setBudgetType] = useState('AI');
  const [aiInput, setAiInput] = useState('');
  const [chosenBudget, setChosenBudget] = useState('');
  const [aiRecommendedBudget, setAiRecommendedBudget] = useState(null);
  
  const [platformBudgets, setPlatformBudgets] = useState([]); // Initialized as an empty arra

  const [error, setError] = useState('');
  const [explanation, setExplanation] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  
  const { addItem } = useCart();
  const navigate = useNavigate();

  const handleBudgetTypeChange = (event) => {
    setBudgetType(event.target.value);
  };

  const handleAiInputChange = (event) => {
    setAiInput(event.target.value);
  };

  const handleBudgetChange = (event) => {
    const value = event.target.value;
    // Allow empty string or numbers
    if (value === '' || !isNaN(value)) {
        setChosenBudget(value);
        // Only call getPlatformBudgets if we have a valid number >= 500
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 500) {
            console.log("💰 Manual Budget Set:", `$${numValue.toLocaleString()}`);
            getPlatformBudgets(numValue);
        }
    }
  };
  
  const getAiRecommendedBudget = async () => {
    try {
        console.log("Analyzing business:", aiInput);

        const openAiResponse = await fetch(`${API_BASE_URL}/api/get-recommended-budget`, {
            method: 'POST',
            headers: await authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                aiInput: aiInput
            }),
        });

        if (!openAiResponse.ok) {
            // Try to parse as text first to handle both JSON and HTML
            const responseText = await openAiResponse.text();
            let errorMessage = `Server error: ${openAiResponse.status} ${openAiResponse.statusText}`;
            
            try {
                const errorData = JSON.parse(responseText);
                errorMessage = errorData.error || errorMessage;
            } catch (parseError) {
                // Response is not JSON (likely HTML error page)
                console.error("Non-JSON error response:", responseText.substring(0, 200));
                if (openAiResponse.status === 405) {
                    errorMessage = 'The /get-recommended-budget endpoint is not available. Please check backend configuration.';
                }
            }
            throw new Error(errorMessage);
        }

        const aiData = await openAiResponse.json();
        
        // Clean logging of recommendations
        console.log("AI Recommendations:");
        console.log(`💰 Recommended Budget: $${aiData.recommendedBudget.toLocaleString()}`);

        setAiRecommendedBudget(aiData.recommendedBudget);
        
        // Store explanation if available
        if (aiData.explanation) {
            setExplanation(aiData.explanation);
            console.log("📝 Explanation received from AI");
        }
        
        // Check if we got platform allocations from OpenAI
        if (aiData.platformAllocations && Object.keys(aiData.platformAllocations).length > 0) {
            // Use OpenAI platform allocations
            console.log("📊 Using AI Platform Allocations:");
            Object.entries(aiData.platformAllocations)
                .sort((a, b) => b[1] - a[1])
                .forEach(([platform, budget]) => {
                    const percentage = ((budget / aiData.recommendedBudget) * 100).toFixed(1);
                    console.log(`   ${platform}: $${budget.toLocaleString()} (${percentage}%)`);
                });
            
            // Transform the data for display
            const budgetsArray = Object.entries(aiData.platformAllocations)
                .map(([platform, budget]) => ({
                    name: platform,
                    budget: budget,
                    logo: platformLogos[platform]
                }))
                .filter(entry => entry.budget > 0)
                .sort((a, b) => b.budget - a.budget);

            setPlatformBudgets(budgetsArray);
        } else {
            // Fall back to random allocation if OpenAI didn't provide platforms
            getPlatformBudgets(aiData.recommendedBudget);
        }

    } catch (error) {
        console.error('❌ Error:', error.message);
        setError(error.message || 'Failed to get AI recommendation');
    }
  };

  const getPlatformBudgets = async (totalBudget) => {
    try {
        // Validate budget before sending
        if (!totalBudget || totalBudget < 500) {
            throw new Error('Minimum budget is $500');
        }

        console.log("Requesting platform allocation for budget:", `$${totalBudget.toLocaleString()}`);

        const response = await fetch(`${API_BASE_URL}/api/allocate-budgets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                totalBudget: parseFloat(totalBudget) 
            }),
        });

        if (!response.ok) {
            // Try to parse as text first to handle both JSON and HTML
            const responseText = await response.text();
            let errorMessage = `Server error: ${response.status} ${response.statusText}`;
            
            try {
                const errorData = JSON.parse(responseText);
                errorMessage = errorData.error || errorMessage;
            } catch (parseError) {
                // Response is not JSON (likely HTML error page)
                console.error("Non-JSON error response:", responseText.substring(0, 200));
                if (response.status === 405) {
                    errorMessage = 'The /allocate-budgets endpoint is not available. Please check backend configuration.';
                }
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        
        // Clean logging for manual budget allocation
        console.log("📊 Platform Allocations:");
        Object.entries(data)
            .sort((a, b) => b[1] - a[1])
            .forEach(([platform, budget]) => {
                const percentage = ((budget / totalBudget) * 100).toFixed(1);
                console.log(`   ${platform}: $${budget.toLocaleString()} (${percentage}%)`);
            });

        // Transform the data for display
        const budgetsArray = Object.entries(data)
            .map(([platform, budget]) => ({
                name: platform,
                budget: budget,
                logo: platformLogos[platform]
            }))
            .filter(entry => entry.budget > 0)
            .sort((a, b) => b.budget - a.budget);

        setPlatformBudgets(budgetsArray);
    } catch (error) {
        console.error('❌ Error:', error.message);
        setError(error.message || 'Failed to get platform budgets');
        setPlatformBudgets([]); // Clear platform budgets on error
    }
  };

  // Calculate the total budget based on the selected budget type
  const totalBudget = budgetType === 'AI' ? aiRecommendedBudget : parseFloat(chosenBudget) || 0;

  const [mediaPlanningFees, setMediaPlanningFees] = useState(0);

  // Fetch media planning fees when the total budget changes
  useEffect(() => {
    if (totalBudget > 0) {
      fetchMediaPlanningFees(totalBudget);
    }
  }, [totalBudget]);

  const fetchMediaPlanningFees = async (totalBudget) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-media-planning-fees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ budget: totalBudget }),
      });
      const data = await response.json();
      setMediaPlanningFees(data.mediaPlanningFees);
    } catch (error) {
      console.error('Error fetching media planning fees:', error);
    }
  };

  const handleAiInputSubmit = () => {
    if (budgetType === 'AI') {
      getAiRecommendedBudget();
    } else {
      // For 'Chose Budget', directly get the platform budgets
      getPlatformBudgets(chosenBudget);
    }
  };

  const subTotal = parseFloat(aiRecommendedBudget || chosenBudget) + parseFloat(mediaPlanningFees);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const handleAddToCart = () => {
    const platformsList = platformBudgets
      .filter(p => p.budget > 0)
      .map(p => `${p.name} (${formatCurrency(p.budget)})`)
      .join(', ');

    const item = {
      id: `ai-paid-ads-${Date.now()}`,
      name: 'AI Recommended Paid Ads Campaign',
      platform: 'AI-Optimized Multi-Platform',
      price: subTotal,
      description: `${budgetType === 'AI' ? 'AI Recommended' : 'Custom Budget'}: ${platformsList}`,
      type: 'paid-ads-ai'
    };
    addItem(item);
    alert('Item added to cart!');
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const getForecast = async () => {
    // If forecast is already showing, just toggle it off
    if (showForecast && forecastData) {
      setShowForecast(!showForecast);
      return;
    }
    
    // If we have cached forecast data, just show it
    if (forecastData) {
      setShowForecast(true);
      return;
    }
    
    if (isForecastLoading) return;
    
    setIsForecastLoading(true);
    setError('');
    
    try {
      // Prepare platform allocations as object
      const platformAllocationsObj = {};
      platformBudgets.forEach(p => {
        if (p.budget > 0) {
          platformAllocationsObj[p.name] = p.budget;
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/get-marketing-forecast`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          businessDescription: aiInput,
          totalBudget: totalBudget,
          platformAllocations: platformAllocationsObj
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setForecastData(data);
      setShowForecast(true);
      console.log('📊 Forecast data received');
    } catch (error) {
      console.error('❌ Forecast Error:', error.message);
      setError('Failed to generate forecast. Please try again.');
    } finally {
      setIsForecastLoading(false);
    }
  };

  return (
    <div className="paai-options-container">
      <div className="paai-budget-type-section">
        <h3 className="paai-section-title">Budget Type</h3>
        <div className="paai-budget-type-choice">
          <label className={`paai-radio-option ${budgetType === 'AI' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="budgetType"
              value="AI"
              checked={budgetType === 'AI'}
              onChange={handleBudgetTypeChange}
            />
            <span className="paai-radio-label">AI Recommended Budget</span>
          </label>
          <label className={`paai-radio-option ${budgetType === 'Chose' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="budgetType"
              value="Chose"
              checked={budgetType === 'Chose'}
              onChange={handleBudgetTypeChange}
            />
            <span className="paai-radio-label">Chose Budget</span>
          </label>
        </div>
      </div>
  
      {budgetType === 'Chose' && (
        <div className="paai-chosen-budget-container">
          <label htmlFor="chosenBudget" className="paai-budget-label">Enter Your Budget</label>
          <input
            type="number"
            id="chosenBudget"
            value={chosenBudget}
            onChange={handleBudgetChange}
            className="paai-budget-input"
            placeholder="$500 minimum"
            min="500"
          />
        </div>
      )}
  
      <div className="paai-chat-interface">
        <div className="paai-chat-input-container">
          <textarea
            id="aiInput"
            value={aiInput}
            onChange={handleAiInputChange}
            className="paai-chat-input"
            placeholder="Describe your Business or Project. For better accuracy please input your website URL..."
          />
          <div className="paai-input-controls">
            <div className="paai-control-left"></div>
            <button 
              className="paai-submit-button" 
              onClick={handleAiInputSubmit} 
              disabled={budgetType === 'Chose' && chosenBudget < 500}>
              →
            </button>
          </div>
        </div>
      </div>
 
      {error && <div className="paai-error-message">{error}</div>}

      {Object.keys(platformBudgets).length > 0 && (
        <div className="paai-platform-budgets-section">
          <h3 className="paai-budgets-title">Recommended Platform Allocation</h3>
          <div className="paai-platform-cards">
            {platformBudgets.map((entry) => (
              entry.budget > 0 && (
                <div key={entry.name} className="paai-platform-card">
                  <div className="paai-platform-header">
                    <img src={entry.logo} alt={entry.name} className="paai-platform-logo" />
                    <span className="paai-platform-name">{entry.name}</span>
                  </div>
                  <div className="paai-platform-budget">
                    {formatCurrency(entry.budget)}
                  </div>
                </div>
              )
            ))}
          </div>
          
          {/* See Details and Forecast Buttons */}
          {explanation && (
            <div className="paai-details-section">
              <div className="paai-action-buttons">
                <button 
                  className="paai-see-details-btn"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? '▲ Hide Details' : '▼ See Details'}
                </button>
                
                <button 
                  className="paai-see-forecast-btn"
                  onClick={getForecast}
                  disabled={isForecastLoading}
                >
                  {isForecastLoading ? '⏳ Loading...' : showForecast ? '▲ Hide Forecast' : '📈 See Forecast'}
                </button>
              </div>
              
              {showDetails && (
                <div className="paai-explanation-container">
                  <div className="paai-explanation-block">
                    <h4 className="paai-explanation-title">💰 Budget Rationale</h4>
                    <p className="paai-explanation-text">
                      {explanation.budget_rationale || 'Budget optimized for your business needs.'}
                    </p>
                  </div>
                  
                  <div className="paai-explanation-block">
                    <h4 className="paai-explanation-title">📊 Platform Strategy</h4>
                    <p className="paai-explanation-text">
                      {explanation.platform_reasoning || 'Platforms selected based on your target audience.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Forecast Display */}
          {showForecast && forecastData && (
            <MarketingForecast forecastData={forecastData} />
          )}
        </div>
      )}
    
      {totalBudget > 0 && (
        <div className="paai-budget-summary-container">
          <div className="total-budget-container">
            <span className="budget-label">Paid Advertisement Budget:</span>
            <span className="budget-value">{formatCurrency(totalBudget)}</span>
          </div>
          <div className="media-planning-fees-container">
            <span className="budget-label">Media Planning Fees:</span>
            <span className="budget-value">{formatCurrency(mediaPlanningFees)}</span>
          </div>
          <div className="subtotal-container">
            <span className="budget-label">Sub-Total:</span>
            <span className="budget-value">{formatCurrency(subTotal)}</span>
          </div>
          <div className="pads-action-buttons-container">
            <button className="pads-add-to-cart" onClick={handleAddToCart}>Add to Cart</button>
            <button className="pads-buy-now" onClick={handleBuyNow}>Buy Now</button>
          </div>
        </div>
      )}
    </div>
  );
}
